import React, { useCallback, useState } from 'react';
import { Button, Flex, Heading, Text } from '@vibe/core';
import { format, parseISO } from 'date-fns';
import { TimesheetStatusBadge } from './TimesheetStatusBadge';
import type { TimesheetSubmission } from '../../types/timesheet-approval.types';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';
import { getWorklogs, type Worklog } from '../../api/worklog.api';
import { fetchItemsByIds, fetchBoardsByIds } from '../../api/monday.api';
import {
  createWorkbook, addTitleRow, styleHeaderRow, styleDataRow, styleTotalRow,
  setColumnWidths, downloadWorkbook, round, fillCell, getStatusColors, hexToArgb, COLORS,
} from '../../utils/export-report-excel';

export interface TimesheetDetailHeaderProps {
  submission: TimesheetSubmission;
  userName?: string;
  submitterName?: string;
}

function formatPeriod(start: string, end: string): string {
  try {
    return `${format(parseISO(start), 'MMM d')} — ${format(parseISO(end), 'MMM d, yyyy')}`;
  } catch {
    return `${start} — ${end}`;
  }
}

const TimesheetDetailHeader: React.FC<TimesheetDetailHeaderProps> = ({
  submission,
  userName,
  submitterName,
}) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      // Fetch worklogs for this period
      const worklogs = await getWorklogs({
        userId: submission.userId,
        startDate: submission.periodStart,
        endDate: submission.periodEnd,
      });

      // Resolve item and board names
      const itemIds = [...new Set(worklogs.map((w) => w.mondayItemId))];
      const boardIds = [...new Set(worklogs.map((w) => w.mondayBoardId))];

      const [items, boards] = await Promise.all([
        itemIds.length > 0 ? fetchItemsByIds(itemIds).catch(() => []) : [],
        boardIds.length > 0 ? fetchBoardsByIds(boardIds).catch(() => []) : [],
      ]);

      const itemNameMap = new Map(items.map((i) => [String(i.id), i.name]));
      const boardNameMap = new Map(boards.map((b) => [String(b.id), b.name]));

      // Build Excel
      const wb = createWorkbook();
      const ws = wb.addWorksheet('Timesheet');

      const displayName = userName ?? `User ${submission.userId}`;
      addTitleRow(ws, `Timesheet — ${displayName}`, submission.periodStart, submission.periodEnd);

      // Summary row
      const statusLabel = submission.status.charAt(0).toUpperCase() + submission.status.slice(1);
      const summaryRow = ws.addRow([
        `Status: ${statusLabel}`,
        '',
        `Total: ${round(submission.totalHours)}h`,
        submission.billableHours != null ? `Billable: ${round(submission.billableHours)}h` : '',
        `${submission.worklogCount} entries`,
      ]);
      summaryRow.font = { size: 10, color: { argb: hexToArgb(COLORS.secondaryText) } };
      ws.addRow([]);

      // Header
      const headers = ['Date', 'Board', 'Item', 'Category', 'Billable', 'Hours (h)', 'Description'];
      const headerRow = ws.addRow(headers);
      styleHeaderRow(ws, headerRow);
      setColumnWidths(ws, [12, 22, 22, 16, 10, 12, 30]);

      // Sort worklogs by date
      const sorted = [...worklogs].sort((a, b) => a.date.localeCompare(b.date));

      for (const wl of sorted) {
        const excelRow = ws.addRow([
          wl.date,
          boardNameMap.get(wl.mondayBoardId) ?? `Board #${wl.mondayBoardId}`,
          itemNameMap.get(wl.mondayItemId) ?? `Item #${wl.mondayItemId}`,
          wl.worklogCategoryName ?? '',
          wl.worklogCategoryIsBillable != null ? (wl.worklogCategoryIsBillable ? 'Yes' : 'No') : '',
          round(wl.hours),
          wl.description ?? '',
        ]);
        styleDataRow(excelRow);

        // Center date + hours
        excelRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
        excelRow.getCell(6).alignment = { horizontal: 'center', vertical: 'middle' };
        excelRow.getCell(6).numFmt = '0.00';

        // Color billable cell
        if (wl.worklogCategoryIsBillable === true) {
          fillCell(excelRow.getCell(5), COLORS.statusApprovedBg, COLORS.statusApprovedFont, true);
        } else if (wl.worklogCategoryIsBillable === false) {
          fillCell(excelRow.getCell(5), COLORS.nonBillableBg, COLORS.nonBillableFont);
        }
      }

      // Total row
      const totalRow = ws.addRow([
        'Total', '', '', '', '',
        round(submission.totalHours),
        '',
      ]);
      styleTotalRow(totalRow);
      totalRow.getCell(6).numFmt = '0.00';

      const safeName = displayName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      await downloadWorkbook(wb, `timesheet_${safeName}_${submission.periodStart}_${submission.periodEnd}.xlsx`);
    } catch (err) {
      console.error('Timesheet export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [submission, userName]);

  return (
    <div className="rp-ts-detail-header">
      <Flex justify={Flex.justify.SPACE_BETWEEN} align={Flex.align.CENTER}>
        <Heading type={Heading.types.H3}>
          {userName ?? `User ${submission.userId}`}
        </Heading>
        <Flex gap={Flex.gaps.SMALL} align={Flex.align.CENTER}>
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
          <TimesheetStatusBadge status={submission.status} />
        </Flex>
      </Flex>

      <div className="rp-ts-detail-header__stats">
        <div className="rp-ts-detail-header__stat">
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            Period
          </Text>
          <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
            {formatPeriod(submission.periodStart, submission.periodEnd)}
          </Text>
        </div>
        <div className="rp-ts-detail-header__stat">
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            Total Hours
          </Text>
          <Text type={Text.types.TEXT1} weight={Text.weights.BOLD} style={{ color: '#0073ea' }}>
            {formatHoursMinutes(Number(submission.totalHours))}
          </Text>
        </div>
        {submission.billableHours != null && (
          <>
            <div className="rp-ts-detail-header__stat">
              <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
                Billable
              </Text>
              <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM} style={{ color: '#00854d' }}>
                {formatHoursMinutes(Number(submission.billableHours))}
              </Text>
            </div>
            <div className="rp-ts-detail-header__stat">
              <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
                Non-Billable
              </Text>
              <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM} style={{ color: '#676879' }}>
                {formatHoursMinutes(Number(submission.totalHours) - Number(submission.billableHours))}
              </Text>
            </div>
          </>
        )}
        <div className="rp-ts-detail-header__stat">
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            Entries
          </Text>
          <Text type={Text.types.TEXT1} weight={Text.weights.MEDIUM}>
            {submission.worklogCount}
          </Text>
        </div>
      </div>

      <div className="rp-ts-detail-header__meta">
        {submitterName && submission.submittedBy !== submission.userId && (
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            Submitted by {submitterName}
          </Text>
        )}
        {submission.submittedAt && (
          <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
            {submission.submittedBy !== submission.userId ? 'on ' : 'Submitted '}
            {format(new Date(submission.submittedAt), 'MMM d, yyyy \'at\' h:mm a')}
          </Text>
        )}
        {submission.submissionNote && (
          <div className="rp-ts-detail-header__note">
            <Text type={Text.types.TEXT2} weight={Text.weights.MEDIUM}>
              Note:
            </Text>
            <Text type={Text.types.TEXT2}>
              {submission.submissionNote}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimesheetDetailHeader;
